import { Popover, Tag, Tooltip } from 'antd';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useColorScheme } from '../../../hooks/useColorScheme/useColorScheme';
import { useTags } from '../../../hooks/useTags';
import type File from '../../../interfaces/File';
import { getVisibleCatalogTagsForFile } from '../../../lib/fileVisibleTags/fileVisibleTags';
import { getTagPresentationStyle } from '../../../utils/colorsUtilis';
import TruncatedTooltip from '../../Common/TruncatedTooltip';
import { Icon } from '../../Core/Icon';

type Props = {
  file: File;
};

/**
 * Grid card status-row control: Tags icon + popover listing visible tags.
 * Avoids growing the card with inline tag pills.
 */
const FilesGridTagsIcon: React.FC<Props> = ({ file }) => {
  const { t } = useTranslation();
  const { tags } = useTags();
  const { resolvedColorScheme } = useColorScheme();
  const isDarkMode = resolvedColorScheme === 'dark';
  const usedForVisibilityLabel = t('common.tagIsUsedForFileVisibility');

  const visibleFileTags = useMemo(() => getVisibleCatalogTagsForFile(file, tags), [file, tags]);

  if (visibleFileTags.length === 0) {
    return null;
  }

  return (
    <Popover
      trigger={['hover', 'click']}
      placement="bottom"
      content={
        <div
          className="flex max-w-56 flex-col gap-2"
          data-testid="files-grid-tags-popover"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          {visibleFileTags.map((tag) => (
            <Tag
              key={tag.id}
              className="m-0!"
              style={getTagPresentationStyle(tag.color, { darkMode: isDarkMode })}
            >
              <div className="flex max-w-full min-w-0 flex-row items-center gap-1">
                <span className="min-w-0 flex-1 overflow-hidden">
                  <TruncatedTooltip className="min-w-0">{tag.title}</TruncatedTooltip>
                </span>
                <div className="flex shrink-0 flex-row items-center gap-0.5">
                  {tag.isUsedForFileVisibility ? (
                    <Tooltip title={usedForVisibilityLabel}>
                      <div>
                        <Icon name="Share2Icon" className="text-gray-dark h-3 w-3" />
                      </div>
                    </Tooltip>
                  ) : null}
                  {tag.publicTag ? (
                    <Icon name="BuildingIcon" className="text-gray-dark h-3 w-3" />
                  ) : null}
                </div>
              </div>
            </Tag>
          ))}
        </div>
      }
    >
      <Tooltip title={t('common.tags')}>
        <button
          type="button"
          data-testid="files-grid-tags-icon"
          aria-label={t('a11y.fileTagsCount', { count: visibleFileTags.length })}
          className="text-blue-dark inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-0"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <Icon name="TagsIcon" className="h-5 w-5" />
        </button>
      </Tooltip>
    </Popover>
  );
};

export default FilesGridTagsIcon;
